import { useEffect, useState } from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetCasesBySupplier } from 'api/kpi/kpi';
import { useExportExportCasesBySupplierHook } from 'api/export/export';
import { KPICasesBySupplierDataPoint } from 'api/model';
import { maxKPIDateRangeMs } from 'constants/AppConstants';

type SortField = 'supplierName' | 'totalCases' | 'percentage';
type SortDirection = 'asc' | 'desc';

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#A28EFF',
  '#FF6699',
  '#33B679',
  '#E67E22',
  '#9B59B6',
  '#1ABC9C',
  '#E74C3C',
  '#3498DB',
];

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const CasesBySupplierWidget = () => {
  const [sortField, setSortField] = useState<SortField>('totalCases');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getKPIData = useKPIGetCasesBySupplier();

  const startDate = daterange[0];
  const endDate = daterange[1];
  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  const isDateRangeTooLong =
    startDate &&
    endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  useEffect(() => {
    if (startDate && endDate && !isDateRangeTooLong) {
      getKPIData.mutate({
        data: {
          startDate,
          endDate,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
    excludeCompanyNameContains,
    isDateRangeTooLong,
  ]);

  const downloadExcel = useExportExportCasesBySupplierHook();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = (
    data: KPICasesBySupplierDataPoint[]
  ): KPICasesBySupplierDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'supplierName':
          aVal = a.supplierName || '';
          bVal = b.supplierName || '';
          break;
        case 'totalCases':
          aVal = a.totalCases;
          bVal = b.totalCases;
          break;
        case 'percentage':
          aVal = a.percentage;
          bVal = b.percentage;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  };

  const currentData = getKPIData.data?.dataPoints ?? [];
  const totalCasesInPeriod = getKPIData.data?.totalCasesInPeriod ?? 0;
  const sortedData = sortData(currentData);

  const chartData = [...currentData].sort(
    (a, b) => b.totalCases - a.totalCases
  );

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.CasesBySupplier]}
      count={totalCasesInPeriod}
      isLoading={getKPIData.isLoading}
      info="Visar fördelningen av ärenden per leverantör inom valt datumintervall. Baserat på ärendets stängd-datum."
    >
      <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
        OBS: Datumen utgår från ärendets stängd-datum.
      </p>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={
            isDownloading || (!isDateRangeTooLong && currentData.length === 0)
          }
          onClick={async () => {
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                startDate: daterange[0]!,
                endDate: daterange[1]!,
                supplierIDs: selectedSupplierIDs,
                customerNames: selectedCustomerNames,
                fromCountyIDs: selectedFromCountyIDs,
                toCountyIDs: selectedToCountyIDs,
                excludeCompanyNameContains:
                  excludeCompanyNameContains ?? undefined,
              });
              if (result) {
                downloadBlob(
                  result,
                  `Nyckeltal_ArendenPerLeverantor_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      {isDateRangeTooLong ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>
            Datumintervallet är för långt för att visa data. Använd
            nedladdningsknappen för att exportera data.
          </p>
        </div>
      ) : sortedData.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getKPIData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa för vald filtrering.</p>
          )}
        </div>
      ) : (
        <>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="totalCases"
                  nameKey="supplierName"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry: KPICasesBySupplierDataPoint) =>
                    `${entry.percentage.toFixed(1)}%`
                  }
                >
                  {chartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value} ärenden`,
                    name,
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead
                style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
              >
                <tr>
                  <th
                    onClick={() => handleSort('supplierName')}
                    style={{
                      cursor: 'pointer',
                      padding: '8px',
                      textAlign: 'left',
                      borderBottom: '2px solid #ddd',
                    }}
                  >
                    Leverantör{' '}
                    <SortIndicator
                      field="supplierName"
                      sortField={sortField}
                      sortDirection={sortDirection}
                    />
                  </th>
                  <th
                    onClick={() => handleSort('totalCases')}
                    style={{
                      cursor: 'pointer',
                      padding: '8px',
                      textAlign: 'right',
                      borderBottom: '2px solid #ddd',
                    }}
                  >
                    Antal{' '}
                    <SortIndicator
                      field="totalCases"
                      sortField={sortField}
                      sortDirection={sortDirection}
                    />
                  </th>
                  <th
                    onClick={() => handleSort('percentage')}
                    style={{
                      cursor: 'pointer',
                      padding: '8px',
                      textAlign: 'right',
                      borderBottom: '2px solid #ddd',
                    }}
                  >
                    Andel{' '}
                    <SortIndicator
                      field="percentage"
                      sortField={sortField}
                      sortDirection={sortDirection}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((dp, index) => (
                  <tr
                    key={`${dp.supplierID ?? 'null'}-${index}`}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={{ padding: '8px' }}>{dp.supplierName}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {dp.totalCases}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {dp.percentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #ddd', fontWeight: 'bold' }}>
                  <td style={{ padding: '8px' }}>Totalt</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {totalCasesInPeriod}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Widget>
  );
};

export default CasesBySupplierWidget;
